import {
  Home,
  Building2,
  Users,
  Settings,
  FileText,
  UserCog,
  List,
  CheckSquare,
  Calendar,
  Clock,
  Utensils,
  GraduationCap,
  Activity,
  Megaphone,
  ClipboardCheck,
  Coins,
  Award,
  Mail,
  Inbox,
  ClipboardList,
  Flame,
  UserPlus,
  CalendarOff,
  ShieldCheck,
  NotebookPen,
  ListChecks,
  Briefcase,
  Wrench,
} from 'lucide-react';

/**
 * Every assignable menu code and where it goes in the dashboard.
 *
 * Single source of truth, shared by the sidebar (which draws these as nav
 * items) and by the post-login landing logic (which picks the first one the
 * user actually holds). Keeping one table is the point: whoever is granted a
 * module can always be landed on it, and a new module only has to be added
 * here once.
 *
 * `careHomeOnly: true` marks a module that only makes sense for a care home
 * (a fixed site). Those are filtered out for domiciliary orgs — the same
 * care-home/domiciliary axis used for the Residents→Clients rename.
 *
 * `hiddenFromSidebar: true` marks a module whose route still works but which
 * is deliberately not drawn in the sidebar. It is therefore never chosen as a
 * landing page either — the user would have nothing to navigate away with.
 */
export const menuCodeMap = {
  // The platform admin's own Dashboard menu (menus.is_admin = true). The
  // org-side twin that managers and staff actually hold is
  // 'manager_dashboard' below.
  'dashboard':      { route: '/manager/dashboard',       icon: Home,          label: 'Dashboard',          priority: 1  },
  // The assignable manager dashboard. Ticking it in Add Staff > Module Access
  // is what gives somebody the dashboard overview and makes it their landing
  // page; without it they land on the first module they do have. Drawn by the
  // sidebar's own fixed Dashboard button rather than as a menu item, so it is
  // marked hidden here to avoid a duplicate row.
  'manager_dashboard': { route: '/manager/dashboard',    icon: Home,          label: 'Dashboard',          priority: 1, hiddenFromSidebar: true },
  // To-Do lives in the top navbar (HeaderTodo popover), so it is not a sidebar
  // item. The /manager/todos route still backs the popover's "View all".
  'todo':           { route: '/manager/todos',           icon: CheckSquare,   label: 'To Do list',         priority: 2, hiddenFromSidebar: true },
  // Care Planner — plan resident care and allocate it to carers, who see it
  // in the app's My Shift tab. Care home only: a domiciliary org plans visits
  // in its Rota. Drawn straight under Dashboard.
  'care_planner':   { route: '/manager/care-planner',    icon: ListChecks,    label: 'Care Planner',       priority: 1.5, careHomeOnly: true },
  'residents':      { route: '/manager/residences',      icon: Users,         label: 'Residents',          priority: 3  },
  // Care Records — a sidebar group (Care Notes + Handovers). A sub-menu of Residents
  // in the access pickers (utils/menuTree.js); drawn right under it here.
  'care_notes':     { route: '/manager/care-notes',      icon: NotebookPen,   label: 'Care Records',       priority: 3.1 },
  // Enquiries & Assessments — the step BEFORE the Enquiry List: an enquiry is
  // taken, an outside assessor completes the linked assessment, and the manager
  // records the outcome. Drawn inside the Residents group (see Sidebar.js), and
  // a sub-menu of Residents in the access pickers (utils/menuTree.js).
  'enquiries':      { route: '/manager/enquiries-assessments', icon: ClipboardList, label: 'Enquiries & Assessments', priority: 3.05 },
  'duty_roster':    { route: '/manager/duty-roster',     icon: Clock,         label: 'Rota',               priority: 4  },
  'staffs':         { route: '/manager/staffs',          icon: UserCog,       label: 'Staff',              priority: 5  },
  // Staff sub-menus. Assignable on their own, so somebody can be given
  // Recruitment without the whole Staff module. When the parent Staff menu IS
  // granted they are dropped from the top level and appear inside that group
  // instead (see the filter in Sidebar.js and staffChildren there).
  'recruitment':      { route: '/manager/recruitment',      icon: UserPlus,       label: 'Recruitment',      priority: 5.1 },
  'staff_compliance': { route: '/manager/staff-compliance', icon: ClipboardCheck, label: 'Staff Compliance', priority: 5.2 },
  'payroll':          { route: '/manager/payroll',          icon: Coins,          label: 'Payroll',          priority: 5.3 },
  // Agencies — book cover from staffing agencies. Care homes only; a Staff
  // sub-menu like Payroll (managers hold it by role, others by grant).
  'agencies':         { route: '/manager/agencies',         icon: Briefcase,      label: 'Agencies',         priority: 5.4, careHomeOnly: true },
  'training':       { route: '/manager/training',        icon: Award,         label: 'Training',           priority: 6  },
  'events':         { route: '/manager/events',          icon: Calendar,      label: 'Events',             priority: 7  },
  'announcement':   { route: '/manager/announcements',   icon: Megaphone,     label: 'Announcements',      priority: 8  },
  'announcements':  { route: '/manager/announcements',   icon: Megaphone,     label: 'Announcements',      priority: 8  },
  // Staffs Report — both care-home and domiciliary managers.
  'staffs_reports': { route: '/manager/staffs-report',   icon: ClipboardList, label: 'Staffs Report',      priority: 9  },
  'staffs_report':  { route: '/manager/staffs-report',   icon: ClipboardList, label: 'Staffs Report',      priority: 9  },
  // The maintenance team's slice of Staffs Report (same page, server-scoped to
  // the maintenance reports). Hidden in the sidebar for anyone who also holds
  // the full Staffs Report menu — see Sidebar.js.
  'staff_reports_maintenance': { route: '/manager/staffs-report', icon: Wrench, label: 'Maintenance reports', priority: 9.01 },
  // Leave & Approval — approving the organisation's time-off requests. An
  // assignable menu, so an admin decides who gets it; applying for your own
  // leave is separate and always available from the account menu.
  'leave_approval': { route: '/manager/leave-approvals', icon: CalendarOff, label: 'Leave & Approval', priority: 9.5 },
  'leave_approvals':{ route: '/manager/leave-approvals', icon: CalendarOff, label: 'Leave & Approval', priority: 9.5 },
  // Audits & policies — quality audits, their follow-up actions and the
  // versioned policy library. Assignable like any other module, so a manager
  // can hand it to a senior carer or quality lead; managers hold it by role
  // as well (see Sidebar.js), so one that predates the menu is never locked
  // out of their own audits.
  'audits_policies': { route: '/manager/audits-policies', icon: ShieldCheck, label: 'Audits & Policies', priority: 9.7 },
  // Evacuation Plan — care home only (a domiciliary org has no site to evacuate).
  'evacuation_plan':  { route: '/manager/evacuation-plan', icon: Flame,       label: 'Evacuation Plan',    priority: 10, careHomeOnly: true },
  'evacuation_plan_': { route: '/manager/evacuation-plan', icon: Flame,       label: 'Evacuation Plan',    priority: 10, careHomeOnly: true },
  // E-Learning and Domestics are hidden from the sidebar per request; their
  // routes still exist for any direct links.
  'e-learning':     { route: '/manager/e-learning',      icon: GraduationCap, label: 'E-Learning',         priority: 11, hiddenFromSidebar: true },
  'domestics':      { route: '/manager/domestics',       icon: Utensils,      label: 'Domestics',          priority: 12, hiddenFromSidebar: true },
  'admin_dashboard':{ route: '/dashboard',               icon: Settings,      label: 'Admin Dashboard',    priority: 13 },
  'Organisation':   { route: '/organizations',           icon: Building2,     label: 'Organizations',      priority: 14 },
  // The platform admin creates staffing agencies and invites their administrator.
  'agencies_admin': { route: '/agencies',                icon: Briefcase,     label: 'Agencies',           priority: 14.5 },
  'plan':           { route: '/plans',                   icon: FileText,      label: 'Subscription Plans', priority: 15 },
  'menu':           { route: '/menus',                   icon: List,          label: 'Menus',              priority: 16 },
  'contact_submissions': { route: '/contact-submissions', icon: Mail,         label: 'Contact Enquiries',  priority: 17 },
  // Outreach email sent and received as sameer@servpatch.co.uk (admin only).
  'outreach_mail':  { route: '/mailbox',                 icon: Inbox,         label: 'Mailbox',            priority: 17.5 },
  'settings':       { route: '/settings',                icon: Settings,      label: 'Settings',           priority: 18 },
  'audit_logs':     { route: '/audit-logs',              icon: Activity,      label: 'Audit Logs',         priority: 19 },
};

/**
 * The assignable menu that grants the manager dashboard.
 *
 * Not the admin's `dashboard` code: that row is menus.is_admin = true and is
 * never offered to an organisation's users, which is why the manager dashboard
 * could not be granted to anybody before this menu existed.
 */
export const MANAGER_DASHBOARD_MENU_CODE = 'manager_dashboard';

/**
 * Roles that hold the dashboard by being that role.
 *
 * A manager always has the dashboard overview whether or not the menu is on
 * their record, so a manager created before the Dashboard menu existed can
 * never be locked out of their own dashboard. Mirrors MANAGER_ROLES in the
 * API's middleware/carehome_manager/auth.js.
 */
export const MANAGER_ROLE_CODES = ['carehome_manager', 'domiciliary_manager'];

/**
 * Account owners — created by the platform admin, one per organisation. They
 * hold everything a manager holds (so isManagerRole is true for them too) and
 * are the only ones who can add a manager, from Add Staff.
 */
export const OWNER_ROLE_CODES = ['carehome_owner', 'domiciliary_owner'];

export const isOwnerRole = (user) =>
  OWNER_ROLE_CODES.includes(user?.role?.role_code);

export const isManagerRole = (user) =>
  MANAGER_ROLE_CODES.includes(user?.role?.role_code) || isOwnerRole(user);

export const isAdminRole = (user) =>
  ['admin', 'super_admin'].includes(user?.role?.role_code);

/**
 * A staffing agency's administrator (created by the platform admin). They get
 * the agency workspace under /agency — never the care-home modules — and the
 * API refuses them everywhere else regardless.
 */
export const AGENCY_ADMIN_ROLE_CODE = 'agency_owner';
export const isAgencyAdminRole = (user) => user?.role?.role_code === AGENCY_ADMIN_ROLE_CODE;
export const AGENCY_HOME_ROUTE = '/agency/overview';

/**
 * May this user see the manager dashboard overview?
 *
 * True for managers (by role, see above) and for anybody granted the
 * Dashboard module — a `staffs` user or a carer whose manager ticked it.
 * Everybody else has no dashboard: they land on, and are limited to, the
 * modules they were actually given.
 */
export const hasDashboardMenu = (user, allAvailableMenus = [], isMenuActive = () => false) => {
  // The platform admin has their own dashboard (/dashboard) and their own
  // admin-only menu list, so they are never gated on the org-side module.
  if (isAdminRole(user)) return true;
  if (isManagerRole(user)) return true;

  return (allAvailableMenus || []).some(
    (menu) => menu.code === MANAGER_DASHBOARD_MENU_CODE && isMenuActive(menu.id),
  );
};

/**
 * Where does this user land after signing in?
 *
 * Granted the Dashboard module (or a manager) → the dashboard overview.
 * Otherwise → the first item their own sidebar would draw, i.e. the lowest
 * priority module they actually hold. That way nobody is ever dropped on a
 * page they were not granted, and nobody is dropped on a page with no way out.
 *
 * Returns null only when they hold nothing at all — the API refuses that login
 * anyway, so the caller can treat it as "no dashboard access".
 */
export const resolveLandingRoute = (
  user,
  allAvailableMenus = [],
  isMenuActive = () => false,
  { isDomiciliary = false } = {},
) => {
  if (isAdminRole(user)) return '/dashboard';
  if (isAgencyAdminRole(user)) return AGENCY_HOME_ROUTE;
  if (hasDashboardMenu(user, allAvailableMenus, isMenuActive)) return '/manager/dashboard';

  const candidates = (allAvailableMenus || [])
    .filter((menu) => isMenuActive(menu.id))
    .map((menu) => ({ code: menu.code, info: menuCodeMap[menu.code] }))
    .filter(({ info }) => info && !info.hiddenFromSidebar)
    // Same care-home-only filter the sidebar applies, so a domiciliary user is
    // never landed on a module their sidebar hides.
    .filter(({ info }) => !(info.careHomeOnly && isDomiciliary))
    .sort((a, b) => a.info.priority - b.info.priority);

  if (!candidates.length) return null;

  const { code, info } = candidates[0];
  // Domiciliary managers don't use the care-home duty roster — their "Rota"
  // opens the domiciliary schedule directly (mirrors Sidebar.js).
  if (code === 'duty_roster' && isDomiciliary) return '/manager/domiciliary-schedule';

  return info.route;
};

export default menuCodeMap;
